import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { playlistService, Playlist, PlaylistWithStatus, CreatePlaylistDto } from '../services/playlistService';

interface PlaylistState {
  // State
  playlists: Playlist[];
  playlistsWithStatus: PlaylistWithStatus[];
  selectedPlaylist: Playlist | null;
  loading: boolean;
  error: string | null;

  // Modal state
  isModalOpen: boolean;
  modalCourseId: string | null;

  // Actions
  fetchMyPlaylists: () => Promise<void>;
  fetchPlaylistsWithCourseStatus: (courseId: string) => Promise<void>;
  fetchPlaylistById: (id: string) => Promise<void>;
  createPlaylist: (data: CreatePlaylistDto) => Promise<Playlist>;
  updatePlaylist: (id: string, data: Partial<CreatePlaylistDto>) => Promise<Playlist>;
  deletePlaylist: (id: string) => Promise<void>;
  addCourseToPlaylist: (playlistId: string, courseId: string) => Promise<void>;
  removeCourseFromPlaylist: (playlistId: string, courseId: string) => Promise<void>;

  // Modal actions
  openModal: (courseId: string) => void;
  closeModal: () => void;

  // Clear state
  clearError: () => void;
}

export const usePlaylistStore = create<PlaylistState>()(
  devtools(
    (set, get) => ({
      // Initial state
      playlists: [],
      playlistsWithStatus: [],
      selectedPlaylist: null,
      loading: false,
      error: null,
      isModalOpen: false,
      modalCourseId: null,

      // Actions
      fetchMyPlaylists: async () => {
        set({ loading: true, error: null });
        try {
          const playlists = await playlistService.getMyPlaylists();
          set({ playlists, loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch playlists',
          });
        }
      },

      fetchPlaylistsWithCourseStatus: async (courseId: string) => {
        set({ loading: true, error: null });
        try {
          const playlistsWithStatus = await playlistService.getPlaylistsWithCourseStatus(courseId);
          set({ playlistsWithStatus, loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch playlists',
          });
        }
      },

      fetchPlaylistById: async (id: string) => {
        set({ loading: true, error: null });
        try {
          const playlist = await playlistService.getById(id);
          set({ selectedPlaylist: playlist, loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch playlist',
          });
        }
      },

      createPlaylist: async (data: CreatePlaylistDto) => {
        set({ loading: true, error: null });
        try {
          const newPlaylist = await playlistService.create(data);
          const { playlists } = get();
          set({
            playlists: [newPlaylist, ...playlists],
            loading: false,
          });
          return newPlaylist;
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to create playlist',
          });
          throw error;
        }
      },

      updatePlaylist: async (id: string, data: Partial<CreatePlaylistDto>) => {
        set({ loading: true, error: null });
        try {
          const updatedPlaylist = await playlistService.update(id, data);
          const { playlists, selectedPlaylist } = get();
          set({
            playlists: playlists.map((p) => (p.id === id ? updatedPlaylist : p)),
            selectedPlaylist: selectedPlaylist?.id === id ? updatedPlaylist : selectedPlaylist,
            loading: false,
          });
          return updatedPlaylist;
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to update playlist',
          });
          throw error;
        }
      },

      deletePlaylist: async (id: string) => {
        set({ loading: true, error: null });
        try {
          await playlistService.delete(id);
          const { playlists } = get();
          set({
            playlists: playlists.filter((p) => p.id !== id),
            loading: false,
          });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to delete playlist',
          });
          throw error;
        }
      },

      addCourseToPlaylist: async (playlistId: string, courseId: string) => {
        set({ loading: true, error: null });
        try {
          await playlistService.addCourseQuick(courseId, playlistId);
          // Refresh playlists with status if modal is open
          const { modalCourseId } = get();
          if (modalCourseId) {
            await get().fetchPlaylistsWithCourseStatus(modalCourseId);
          }
          set({ loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to add course',
          });
          throw error;
        }
      },

      removeCourseFromPlaylist: async (playlistId: string, courseId: string) => {
        set({ loading: true, error: null });
        try {
          const updatedPlaylist = await playlistService.removeCourse(playlistId, courseId);
          const { selectedPlaylist, modalCourseId } = get();

          // Update selectedPlaylist if viewing this playlist
          if (selectedPlaylist?.id === playlistId) {
            set({ selectedPlaylist: updatedPlaylist });
          }

          // Refresh playlists with status if modal is open
          if (modalCourseId) {
            await get().fetchPlaylistsWithCourseStatus(modalCourseId);
          }
          set({ loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to remove course',
          });
          throw error;
        }
      },

      // Modal actions
      openModal: (courseId: string) => {
        set({ isModalOpen: true, modalCourseId: courseId });
        get().fetchPlaylistsWithCourseStatus(courseId);
      },

      closeModal: () => {
        set({ isModalOpen: false, modalCourseId: null, playlistsWithStatus: [] });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'PlaylistStore' }
  )
);

// Utility hooks
export const usePlaylistModal = () =>
  usePlaylistStore((state) => ({
    isOpen: state.isModalOpen,
    courseId: state.modalCourseId,
    playlists: state.playlistsWithStatus,
    loading: state.loading,
    error: state.error,
    open: state.openModal,
    close: state.closeModal,
    addCourse: state.addCourseToPlaylist,
    removeCourse: state.removeCourseFromPlaylist,
    createPlaylist: state.createPlaylist,
  }));
